import { useEffect, useMemo, useRef, useState } from 'react';
import type { EventRecord } from '@lib/care-model';
import { newClientRequestId, sendEmailReply, type EmailReplyRow } from '@lib/care-api';

/**
 * The entry's EMAIL section: Marny answers a member's own email, by email, from the plan's own mailbox
 * (M1 part B, his R. 184, 4 Oct 2026: row 872-1 "YES, Marny may email members from the Care portal",
 * 872-4 "Marny answers WyoBlue email"). Shown only when the deployment has member email on
 * (MEMBERCARE_EMAIL_REPLY) AND the API named this plan's own mailbox AND the entry is a member's own
 * email (memberEmailEntry below, the screen's copy of the route's rule).
 *
 * THE SERVER DECIDES, THE SCREEN SAYS SO. Every refusal (a STOP on file, the brake, a claim, a mailbox
 * Care may not use) comes back as a sentence and is printed as it came; the screen never second-guesses
 * a send. The request id is minted per reply and KEPT in the two cases where reusing it is the safe
 * thing: the brake (nothing reached Graph) and an unknown outcome (a second press replays the first
 * answer and can never send a second email).
 */

/** The sources whose entries are a member's own email: the route's MEMBER_EMAIL_SOURCES, kept in step. */
export const MEMBER_EMAIL_SOURCES: readonly string[] = ['', 'member', 'help-email'];

/** The screen's copy of care/email-reply.ts memberEmailRefusal: an inbound Email entry from the member. */
export function memberEmailEntry(ev: Pick<EventRecord, 'channel' | 'direction' | 'source' | 'fromAddress'>): boolean {
  return (ev.direction || 'inbound') === 'inbound'
    && ev.channel === 'Email'
    && MEMBER_EMAIL_SOURCES.includes(String(ev.source ?? ''))
    && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(ev.fromAddress ?? '').trim());
}

const STATUS_WORDS: Record<EmailReplyRow['status'], { label: string; pill: string }> = {
  sent: { label: 'Sent', pill: 'pill-success' },
  'not-sent': { label: 'Not sent', pill: 'pill-danger' },
  unknown: { label: 'Outcome unknown', pill: 'pill-warning' },
  pending: { label: 'Outcome unknown', pill: 'pill-warning' },
};

function when(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export default function EmailReplyPanel({ ev, mailbox, locked, onSent }: {
  ev: EventRecord;
  /** The plan's own mailbox (the API's replyMailbox for the entry's client). */
  mailbox: string;
  /** Claimed by another operator, cleared, archived or otherwise read-only: nothing may be sent. */
  locked: boolean;
  /** Reload the entry after an answer that changed it. */
  onSent: () => void | Promise<void>;
}) {
  const [text, setText] = useState('');
  const [requestId, setRequestId] = useState<string>(() => newClientRequestId());
  const [sending, setSending] = useState(false);
  const [answer, setAnswer] = useState<{ tone: 'ok' | 'warn' | 'danger'; words: string } | null>(null);

  // A different entry is a different reply: a fresh box, a fresh id, no stale answer. Only on a CHANGE
  // of entry, never on mount (the first id is the one minted above).
  const shownEvent = useRef(ev.eventId);
  useEffect(() => {
    if (shownEvent.current === ev.eventId) return;
    shownEvent.current = ev.eventId;
    setText('');
    setRequestId(newClientRequestId());
    setAnswer(null);
  }, [ev.eventId]);

  const history = useMemo(() => [...(ev.emailReplies ?? [])].reverse(), [ev.emailReplies]);
  const subject = ev.subject ? (/^re:/i.test(ev.subject) ? ev.subject : `RE: ${ev.subject}`) : 'RE: your message';

  const send = async () => {
    const words = text.trim();
    if (!words || sending || locked) return;
    setSending(true);
    setAnswer(null);
    try {
      const r = await sendEmailReply({ id: ev.eventId, text: words, clientRequestId: requestId });
      const d = r.data as { status?: string; message?: string; error?: string; replay?: boolean };
      const sentence = String(d.message ?? d.error ?? `The console answered ${r.status}.`);
      const status = String(d.status ?? '');
      if (r.ok && status === 'sent') {
        setAnswer({ tone: 'ok', words: sentence });
        setText('');
        setRequestId(newClientRequestId());
      } else if (r.status === 429 || status === 'unknown' || status === 'pending') {
        // The brake (nothing reached Graph) and an unknown outcome: KEEP the id. A second press is safe.
        setAnswer({ tone: 'warn', words: sentence });
      } else {
        setAnswer({ tone: r.status >= 500 ? 'danger' : 'warn', words: sentence });
        setRequestId(newClientRequestId());
      }
      if (status === 'sent' || status === 'not-sent' || status === 'unknown' || d.replay) await onSent();
    } catch (err) {
      // The request itself failed: the server may or may not have it, so the id is KEPT and a second
      // press replays rather than sends twice.
      setAnswer({ tone: 'danger', words: `No answer from the console (${String((err as Error)?.message ?? err)}). Press Send again: the same reply is never sent twice.` });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="email-reply-panel">
      <div className="small" data-testid="email-reply-from">From: <strong>{mailbox}</strong></div>
      <div className="small" data-testid="email-reply-to">To: <strong>{ev.fromAddress}</strong></div>
      <div className="small" data-testid="email-reply-subject">Subject: {subject}</div>
      <blockquote
        className="muted"
        data-testid="email-reply-quote"
        style={{ margin: '6px 0', padding: '4px 8px', borderLeft: '3px solid var(--border, #ccc)', maxHeight: 160, overflow: 'auto', whiteSpace: 'pre-wrap' }}
        title="The member's message: it stays beneath the reply, in the same conversation"
      >
        {ev.content || '(the member\'s message has no text)'}
      </blockquote>
      <textarea
        className="textarea-input"
        aria-label="Email reply"
        data-testid="email-reply-text"
        rows={6}
        style={{ resize: 'vertical' }}
        value={text}
        disabled={locked || sending}
        placeholder={locked ? 'This entry is read-only here.' : 'Write the reply. The member\'s message stays beneath it.'}
        onChange={(e) => setText(e.target.value)}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          data-testid="email-reply-send"
          disabled={locked || sending || !text.trim()}
          onClick={() => void send()}
          title={`Send from ${mailbox} to ${ev.fromAddress}`}
        >
          {sending ? 'Sending...' : 'Send email'}
        </button>
        <span className="small muted">Checked for STOP before it goes; one reply per press.</span>
      </div>
      {answer && (
        <p className={answer.tone === 'ok' ? 'sms-notice sms-notice-ok' : answer.tone === 'warn' ? 'sms-notice sms-notice-warn' : 'sms-notice sms-notice-error'} data-testid="email-reply-answer" role="status">
          {answer.words}
        </p>
      )}
      {history.length > 0 && (
        <table className="small" data-testid="email-reply-history" style={{ marginTop: 6, width: '100%' }}>
          <thead>
            <tr><th>When</th><th>By</th><th>Outcome</th><th>Reply</th></tr>
          </thead>
          <tbody>
            {history.map((h) => {
              const sw = STATUS_WORDS[h.status] ?? STATUS_WORDS.unknown;
              return (
                <tr key={h.clientRequestId}>
                  <td className="small">{when(h.sentAt ?? h.at)}</td>
                  <td className="small">{h.byName || h.by}</td>
                  <td><span className={`pill ${sw.pill}`} title={h.reason ?? ''}>{sw.label}</span></td>
                  <td className="small" style={{ whiteSpace: 'pre-wrap' }}>{h.text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
